import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';

type UnpurchasedDomain = {
  id: string;
  domain_name: string;
  created_domain?: string | null;
  created_at?: string;
  bubble_creation_date?: string;
};

export const UnpurchasedOrders: React.FC = () => {
  const [domains, setDomains] = useState<UnpurchasedDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Default cutoff: 2025-10-09T04:00:00.000Z (12:00am ET)
  const defaultCutoffIso = '2025-10-09T04:00:00.000Z';

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getUnpurchasedDomains(defaultCutoffIso);
      if (res.success) {
        setDomains(res.data);
      } else {
        setError(res.message || 'Failed to load domains');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const byTld = useMemo(() => {
    const tldMap: Record<string, string[]> = { '.com': [], '.co': [], '.org': [] };
    for (const d of domains) {
      const name = d.domain_name?.toLowerCase() || '';
      if (name.endsWith('.com')) tldMap['.com'].push(name);
      else if (name.endsWith('.co')) tldMap['.co'].push(name);
      else if (name.endsWith('.org')) tldMap['.org'].push(name);
    }
    return tldMap;
  }, [domains]);

  const copyList = async (tld: '.com' | '.co' | '.org') => {
    const text = byTld[tld].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tld);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('Copy failed');
    }
  };

  const markAllAsOrdered = async () => {
    if (domains.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const names = domains.map(d => d.domain_name);
      const res = await apiService.markDomainsOrdered(names);
      if (!res.success) {
        setError(res.message || 'Failed to mark as ordered');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to mark as ordered');
    } finally {
      await refresh();
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Unpurchased Orders</h1>

      <div className="flex items-center gap-3 mb-4">
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => copyList('.com')}
          disabled={loading || byTld['.com'].length === 0}
        >
          Copy .com ({byTld['.com'].length}){copied === '.com' ? ' ✓' : ''}
        </button>
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => copyList('.co')}
          disabled={loading || byTld['.co'].length === 0}
        >
          Copy .co ({byTld['.co'].length}){copied === '.co' ? ' ✓' : ''}
        </button>
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => copyList('.org')}
          disabled={loading || byTld['.org'].length === 0}
        >
          Copy .org ({byTld['.org'].length}){copied === '.org' ? ' ✓' : ''}
        </button>

        <div className="flex-1" />

        <button
          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          onClick={markAllAsOrdered}
          disabled={loading || domains.length === 0}
        >
          Mark All As Ordered
        </button>

        <button
          className="px-3 py-2 border rounded"
          onClick={refresh}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 text-red-600">{error}</div>
      )}

      <div className="bg-white rounded shadow overflow-hidden">
        <div className="px-4 py-2 border-b flex justify-between text-sm text-gray-600">
          <span>Total: {domains.length}</span>
          <span>.com: {byTld['.com'].length} · .co: {byTld['.co'].length} · .org: {byTld['.org'].length}</span>
        </div>
        <ul className="divide-y max-h-[60vh] overflow-auto">
          {loading && (
            <li className="p-4 text-gray-500">Loading...</li>
          )}
          {!loading && domains.length === 0 && (
            <li className="p-4 text-gray-500">No domains found.</li>
          )}
          {!loading && domains.map((d) => (
            <li key={d.id} className="px-4 py-2 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{d.domain_name}</div>
                <div className="text-gray-500">
                  Created: {d.bubble_creation_date || d.created_at || '—'}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UnpurchasedOrders;


